import { useEffect, useState } from 'react';

/**
 * Resolve CSS custom-property colour strings (e.g. "var(--data-1)") to concrete
 * values via getComputedStyle.
 *
 * Recharts renders fills onto SVG elements, where `var()` support is uneven —
 * resolving to a literal colour up front is the robust path. We re-resolve when
 * the theme flips (the [data-theme] attribute on <html> changes), so light/dark
 * stay correct.
 */
export function useResolvedColors(tokens: string[]): string[] {
  const [resolved, setResolved] = useState<string[]>(tokens);

  useEffect(() => {
    const resolve = () => {
      const styles = getComputedStyle(document.documentElement);
      setResolved(
        tokens.map((t) => {
          const match = /^var\((--[^),]+)\)$/.exec(t.trim());
          if (!match) return t;
          const value = styles.getPropertyValue(match[1]).trim();
          return value || t;
        }),
      );
    };

    resolve();

    // Re-resolve on theme toggle (html[data-theme] mutation).
    const observer = new MutationObserver(resolve);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
    // The token list is stable per render; join to compare by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens.join('|')]);

  return resolved;
}
